import { AxisCode, ValueType } from '@atlas/types'

/**
 * Attribute `value_type`s this page renders a control for. `url`/`date` attribute
 * axes are not surfaced — no input for either is introduced by this page.
 */
export const IN_SCOPE_ATTRIBUTE_VALUE_TYPES = [ValueType.TEXT, ValueType.LIST, ValueType.NUMBER]

/**
 * Which attribute axes this page offers at all, on top of the value-type gate.
 *
 * A value-type gate alone is not an axis gate: every seeded `text`/`list`/`number`
 * attribute would become a batch control, `order_code` included — and an
 * `order_code` typed here attaches the file to that work order (the deliverable
 * list is discovered by that attribute alone) with none of the bookkeeping the
 * real attach path performs. So the set of *axes*, not just their value types,
 * has to be stateable.
 *
 * Both fields are optional and default to "no restriction", which keeps the
 * registry-driven behaviour — an axis added by INSERT shows up with no deploy —
 * as the baseline rather than something a list has to be edited to allow.
 * `allow` (when set) is authoritative; `exclude` then subtracts from it.
 */
export interface AttributeAxisPolicy {
	/** When set, ONLY these axis codes are offered. Unset ⇒ every in-scope attribute axis. */
	allow?: string[]
	/** Axis codes never offered, applied after {@link AttributeAxisPolicy.allow}. */
	exclude?: string[]
}

/**
 * The page's attribute policy. Empty on purpose: no restriction is configured, so
 * every in-scope attribute axis is offered. Set `allow` to pin the page to a fixed
 * set, or `exclude` to drop individual axes while staying registry-driven.
 */
export const ATTRIBUTE_AXIS_POLICY: AttributeAxisPolicy = {}

/**
 * Axes shown WITHOUT the user having to add them, the way
 * {@link REQUIRED_DIMENSIONS} are — but not required, so they gate nothing.
 * `useRevealedAxes` already keeps any axis carrying a value visible; this is
 * what puts an empty one on screen in the first place.
 *
 * `asset_type_code` sits here rather than in {@link REQUIRED_DIMENSIONS}: it is
 * worth asking for on every upload, but nothing routes on it - it describes the
 * piece, it does not decide where the file lands - and the detected binary type
 * already says most of what it would say. Shown, so it is answered; not required,
 * so a file is never held back for it.
 */
export const DEFAULT_AXES: string[] = [AxisCode.CAMPAIGN, AxisCode.ASSET_TYPE_CODE]

/**
 * Attribute axes whose values are real ENTITIES rather than `axis_value` rows.
 *
 * `campaign` stores a campaign CODE in a `text` attribute, so its value type says
 * "free text" while the value is really picked from a known set. Listing the axis
 * here routes it to a picker fed by that entity's own API; every other axis keeps
 * being driven purely by `kind`/`value_type`.
 *
 * A map rather than a branch in the panel: adding the next entity-backed axis is
 * an entry here, in the same file as the page's other registry-driven config —
 * not an axis-code check inside a component.
 */
export const ENTITY_BACKED_ATTRIBUTE_AXES: readonly string[] = [AxisCode.CAMPAIGN]

/** Whether an axis takes its options from an entity API instead of `axis_value`. */
export function isEntityBackedAxis(code: string): boolean {
	return ENTITY_BACKED_ATTRIBUTE_AXES.includes(code)
}

/** Whether an attribute axis passes {@link ATTRIBUTE_AXIS_POLICY}. */
export function isAttributeAxisAllowed(code: string, policy: AttributeAxisPolicy = ATTRIBUTE_AXIS_POLICY): boolean {
	if (policy.allow && !policy.allow.includes(code)) return false
	return !policy.exclude?.includes(code)
}

/**
 * Display overrides for the batch classification panel. The panel renders every
 * pickable dimension from the axis registry (nothing here hard-codes the set of
 * axes); this map only overrides the label + column order for the axes shown in
 * the mockup. An axis with no entry falls back to its registry `name` and sorts
 * after the configured ones.
 */
export const AXIS_DISPLAY: Record<string, { label: string; order: number }> = {
	// Geography leads because it narrows what follows: brand options are faceted
	// by the chosen market, so picking it first turns a 196-entry brand list into
	// the handful actually used there.
	geography: { label: 'Geography', order: 1 },
	brand: { label: 'Brand / Product', order: 2 },
	species: { label: 'Species', order: 3 },
	// Named apart from `asset_type_code` on purpose. They are two seeded dimensions
	// that a shared "file type" wording collapses into one: `asset_type_code` is WHAT
	// THE PIECE IS (Article, Video, Infographic), `file_type` is WHAT THE FILE IS
	// FOR (deliverable, source, logo, brandbook). Both are pickable here, so two
	// controls reading the same leaves no way to tell which one is being set.
	// File Type leads because it is the one that gates the commit.
	file_type: { label: 'File Type', order: 4 },
	asset_type_code: { label: 'Asset Type', order: 5 },
	channel: { label: 'Channel', order: 6 },
}

/**
 * Dimensions that must have a resolved value before a file is `ready` to commit.
 *
 * The gate is whose it is (`brand`), where it applies (`geography`), and what
 * the file is FOR (`file_type`) - the three a later read cannot infer. `file_type`
 * is what routes a file: a brand-kit asset (`logo`, `brandbook`, …) versus
 * something produced for an order (`deliverable`, `source`), and brand-kit reads
 * filter on exactly that. An unset role is indistinguishable from a deliberate
 * one, and nothing downstream can recover it, so it is asked for at the only
 * moment the person who knows the answer is present.
 */
export const REQUIRED_DIMENSIONS: string[] = [AxisCode.BRAND, AxisCode.GEOGRAPHY, AxisCode.FILE_TYPE]

/**
 * The only axes whose options are narrowed by the rest of the selection.
 *
 * Faceting is USAGE-derived — it answers "which values already co-occur in
 * classified assets". That is right for a search rail, where you are looking
 * for things that exist, and wrong for a classification form, where you are
 * describing something that does not exist yet: narrowing `asset_type_code` to the
 * types already seen for US + Bravecto removes exactly the option a user needs
 * to file the first video of that pairing.
 *
 * Brand is the exception because the relationship is real — a brand is sold in
 * some markets and not others — and the list is 196 entries long, so faceting
 * it by geography is the difference between a usable picker and a scroll.
 * Every other axis (asset_type_code, channel, species, locale, …) is independent of
 * the others and must always offer its full registry list.
 */
export const FACETED_AXES = ['brand']
